/**
 * Brewing Science Institute (Woodland Park, Colorado) - liquid brewing yeast,
 * Brettanomyces and bacteria.
 *
 * BSI print a specification block on every product page at brewingscience.com
 * - region, apparent attenuation, fermentation range in Fahrenheit,
 * flocculation, alcohol tolerance, STA1 and strain type - and every figure in
 * this file was read off those blocks in September 2026, one page per strain.
 * Temperatures are converted from Fahrenheit and rounded to whole degrees, so
 * a published 64-72 °F range reads 18-22 °C here.
 *
 * BSI are unusual in publishing an STA1 result for the whole catalogue, so
 * `sta1` is never `null` in this file: it is `false` where they print
 * "Negative" and `true` for the thirteen strains they print as positive - the
 * saisons, several of the Trappist and Belgian strong strains, BSI-351
 * Hefeweizen 3 and the B-73 Roselare blend.
 *
 * Product codes are BSI's own. Most strains carry one in the product name
 * (`A-56`, `BSI-830`, `L-24`, `W-68`, `1187`), and there the code is the code
 * and the name is what follows it. The strains BSI sell under a name alone -
 * the monastery lagers, the kveiks, Barbarian, X-Tropica - use that name as
 * the code. Three culture names run past the 24-character code field and are
 * abbreviated in the conventional way (`B. bruxellensis`, `L. delbrueckii`);
 * the Bretts and bacteria are abbreviated as a set so they read alike.
 *
 * BSI list several strains twice, under both a letter code and a BSI number
 * (A-28 and BSI-13, A-75 and BSI-23, 1187 and BSI-5, BSI-810 and L-12, and
 * three separate listings of 34/70). Those are kept as separate entries
 * because BSI sell them as separate products and, in most cases, publish
 * slightly different specifications for each.
 *
 * Not entered: the four "Express" SKUs (A-18-2, Aug-1, BSI-940-1, W177-1).
 * Express is BSI's next-day shipping programme, not a different culture - the
 * A-56 Chico page says so in as many words - and the Express listings carry
 * the same specifications as the strains they duplicate. The lab's enzymes,
 * nutrient, stains, agar plates and wort concentrate are not yeast and are
 * out of scope.
 *
 * Marked `representative`, with the reason in each description:
 *
 * - `bsi-269`, `bsi-louis-p` - BSI publish attenuation as "High" rather than
 *   a band, so the bands here are ours.
 * - `bsi-s-26`, `bsi-b-73` - BSI publish flocculation as "Variable"; both are
 *   entered `low`, because a culture that may stay up is the one to plan for.
 * - `bsi-lactobacillus-brevis`, `bsi-lactobacillus-delbrueckii` - pure
 *   bacteria pitches. BSI publish only a temperature range for each; the
 *   attenuation band is nominal, as on Escarpment's Lactobacillus Blend 2.0,
 *   and flocculation is entered `low`. Their published alcohol tolerance of
 *   0% becomes `null`. L. delbrueckii's published 110-115 °F is 43-46 °C and
 *   the top of the band is held at this catalog's 45 °C ceiling.
 * - `bsi-pediococcus-damnosus` - BSI's own reference guide prints "No Specs
 *   Available" against attenuation, flocculation and temperature for pedio,
 *   so all three are ours; only the 9% alcohol tolerance and the STA1 result
 *   are theirs.
 *
 * Type is BSI's strain type with three deliberate departures. The four
 * Norwegian farmhouse cultures are `kveik` rather than the `Ale / Wild` BSI
 * label them, because their temperature envelope is the whole point. The
 * kolsch and alt strains are `hybrid`. And BSI-810 San Francisco Lager and
 * L-12 Common Lager - the same steam-beer culture, sold twice - are `hybrid`
 * too: BSI file them under lager, but a lager yeast held at 20 °C is exactly
 * what the schema means by hybrid.
 */

import type { Yeast } from '../../schema/yeast';

/** Brewing Science Institute cultures. @see Yeast */
export const bsiYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // American and modern ale
  // -------------------------------------------------------------------------
  {
    id: 'bsi-1',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-1',
    name: 'American Ale 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      "The default American ale strain, and BSI's own answer to a brewery that can keep only one: neutral, strongly attenuative, and content to disappear behind a hop bill. It crops predictably, takes gravity well, and works as convincingly in a stout as in a West Coast IPA.",
    tags: ['american-ale', 'chico', 'clean', 'neutral', 'ipa', 'pale-ale', 'house-strain'],
    url: 'https://brewingscience.com/product/american-ale-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-96',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-96',
    name: 'Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A variant of BSI-1 taken from a brewing institute rather than a brewery, and it behaves like one: clean, neutral, high-attenuating and reliable to harvest. Suited to moderate and high gravity beers where malt and hops should do the talking.',
    tags: ['american-ale', 'clean', 'neutral', 'ipa', 'pale-ale', 'stout'],
    url: 'https://brewingscience.com/product/ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-56',
    lab: 'Brewing Science Institute',
    productCode: 'A-56',
    name: 'Chico Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      "BSI's cut of the Chico strain, the American ale yeast every other one gets measured against. It finishes dry with a soft, low-ester profile and dependable flocculation, and stays neutral enough that a hop-forward pale ale reads exactly as it was designed.",
    tags: ['chico', 'american-ale', 'clean', 'ipa', 'pale-ale', 'amber', 'house-strain'],
    url: 'https://brewingscience.com/product/chico-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-x-tropica',
    lab: 'Brewing Science Institute',
    productCode: 'X-Tropica',
    name: 'X-Tropica',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      "A bioengineered derivative of BSI's A-56 Chico, modified to release thiols already bound up in malt and hops and so lift passion fruit, guava, grapefruit and blackcurrant well above the base strain. It ferments like Chico and can be repitched; BSI suggest pilsner malt, oats and dextrin malt as precursor sources and advise avoiding wheat and rye.",
    tags: [
      'thiol',
      'bioengineered',
      'tropical',
      'ipa',
      'hazy',
      'chico',
      'american-ale',
      'biotransformation',
    ],
    url: 'https://brewingscience.com/product/x-tropica/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-72',
    lab: 'Brewing Science Institute',
    productCode: 'A-72',
    name: 'American Microbrewery Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A San Francisco brewery strain: soft, faintly nutty, and finishing with a touch of tartness. Fruitier and more flocculent than Chico, which makes for an easier harvest and suits both American pale ale and the hazier end of the IPA spectrum.',
    tags: ['american-ale', 'pale-ale', 'hazy', 'ipa', 'nutty', 'soft'],
    url: 'https://brewingscience.com/product/american-microbrewery-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-8',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-8',
    name: 'American Ale 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 73 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "An East Coast American strain that stays largely clean but stops short of Chico's attenuation, leaving body behind to carry bitterness. Useful where a hoppy beer needs a malt spine, and for giving European styles an American accent.",
    tags: ['american-ale', 'east-coast', 'malty', 'full-body', 'pale-ale'],
    url: 'https://brewingscience.com/product/american-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-41',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-41',
    name: 'American Ale 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Pacific Northwest strain with a restrained ester profile and a low attenuation ceiling, which suits it to milds, bitters and porters as readily as to an IPA. Highly flocculent, so expect a bright beer without much coaxing.',
    tags: ['american-ale', 'pacific-northwest', 'subtle', 'mild', 'bitter', 'porter', 'stout'],
    url: 'https://brewingscience.com/product/american-ale-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-51',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-51',
    name: 'American Ale 5',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'More fruit than BSI-1 and a little more inclined to drop, this American strain adds an ester layer that flatters modern hops without tipping into a British profile. Also at home in porters and stouts.',
    tags: ['american-ale', 'fruity', 'ipa', 'pale-ale', 'porter', 'stout'],
    url: 'https://brewingscience.com/product/american-ale-5/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-90',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-90',
    name: 'Southern California Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 83 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A fast, clean Southern Californian strain that outruns both Chico and BSI-1 and carries a higher alcohol tolerance, which is the point: double IPA and imperial stout. Neutral and dry, with a faint sulfur note if the fermentation is stressed.',
    tags: ['american-ale', 'clean', 'dry', 'double-ipa', 'imperial-stout', 'high-gravity', 'fast'],
    url: 'https://brewingscience.com/product/southern-california-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-269',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-269',
    name: 'Michigan Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "A southwest Michigan house strain built for Midwestern IPA and pale ale: gentle citrus and orchard-fruit esters over a light malty finish, balanced rather than assertive, and equally happy in porter, stout or barley wine. BSI publish attenuation only as 'high', so the band here is ours; they give alcohol tolerance as 8–10%.",
    tags: ['american-ale', 'michigan', 'ipa', 'pale-ale', 'citrus', 'balanced'],
    url: 'https://brewingscience.com/product/bsi-269-michigan-ale/',
    provenance: 'representative',
  },
  {
    id: 'bsi-9',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-9',
    name: 'Australian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A hardy warm-fermenting strain that stays clean and malty with a fresh-bread ester note. BSI point it at everything from blonde ale to stout, and it takes fermentation temperatures that would push other ale yeasts into solvent territory.',
    tags: ['australian', 'bready', 'malty', 'blonde-ale', 'stout', 'warm-ferment'],
    url: 'https://brewingscience.com/product/australian-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-cl50',
    lab: 'Brewing Science Institute',
    productCode: 'CL50',
    name: 'California Pub Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 76 },
    temperatureC: { min: 17, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A brewpub strain that leaves a big, round malt palate; threshold diacetyl and gentle esters give it a silky texture that survives a heavy hop charge. Well suited to American red and pale ales.',
    tags: ['american-ale', 'brewpub', 'malty', 'silky', 'red-ale', 'pale-ale'],
    url: 'https://brewingscience.com/product/california-pub-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-barbarian',
    lab: 'Brewing Science Institute',
    productCode: 'Barbarian',
    name: 'Barbarian',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "BSI's hazy-IPA strain: stone fruit, peach and apricot esters that lock onto citrus and tropical hops, with far more cooperative flocculation than most New England yeasts. Many breweries blend it with A-18 London Ale III, and it makes a respectable English pale on its own.",
    tags: ['hazy', 'neipa', 'ipa', 'stone-fruit', 'peach', 'apricot', 'flocculent'],
    url: 'https://brewingscience.com/product/barbarian/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // British and Irish ale
  // -------------------------------------------------------------------------
  {
    id: 'bsi-1187',
    lab: 'Brewing Science Institute',
    productCode: '1187',
    name: 'Ringwood Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 73 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Ringwood strain: malt-forward, distinctly estery, and a natural partner for traditional English malt in brown ales, pales and porters. It wants far more oxygen than most ale yeasts and loses viability quickly in storage, so pitch it fresh and aerate properly.',
    tags: ['ringwood', 'english-ale', 'malty', 'estery', 'brown-ale', 'porter', 'high-oxygen'],
    url: 'https://brewingscience.com/product/ringwood-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-5',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-5',
    name: 'English Ale 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 74 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "BSI's numbered listing of the same Ringwood-type English strain as 1187: malt-forward, finishing estery and fruity over traditional English malt. Oxygen-hungry and short-lived in storage, and published here with a slightly different attenuation band.",
    tags: ['english-ale', 'malty', 'fruity', 'brown-ale', 'pale-ale', 'high-oxygen'],
    url: 'https://brewingscience.com/product/english-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-18',
    lab: 'Brewing Science Institute',
    productCode: 'A-18',
    name: 'London Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'One of the strains modern hazy IPA was built on: genuinely top-cropping, with a fruity ester profile and a soft palate that suits American citrus and tropical hops. It started life as an English pale ale yeast and can leave residual sweetness, though in heavily hopped beers BSI say attenuation creeps toward 80%. Repeated cone cropping shifts the population and its behavior with it.',
    tags: ['neipa', 'hazy', 'ipa', 'english-ale', 'london', 'fruity', 'top-cropping', 'soft'],
    url: 'https://brewingscience.com/product/london-ale-iii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-28',
    lab: 'Brewing Science Institute',
    productCode: 'A-28',
    name: 'London Ale I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A Burton-upon-Trent pub strain with a pronounced mineral character: bold, woody and crisp. Good attenuation and an 11% ceiling let it reach into stronger English beers, and BSI point it at stouts and British pale ales.',
    tags: ['english-ale', 'burton', 'mineral', 'woody', 'stout', 'pale-ale'],
    url: 'https://brewingscience.com/product/london-ale-i/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-13',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-13',
    name: 'English Ale 5',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The same Burton-upon-Trent pub strain BSI also list as A-28, under its numbered name and with a wider published attenuation band. Minerally, bold and woody, crisp on the finish, and comfortable in higher-gravity English styles.',
    tags: ['english-ale', 'burton', 'mineral', 'woody', 'stout', 'pale-ale'],
    url: 'https://brewingscience.com/product/english-ale-5/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-32',
    lab: 'Brewing Science Institute',
    productCode: 'A-32',
    name: 'British Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A British strain that leans malty with a mild fruit note and unusual depth for its class. Attenuation stays low to moderate, which is the reason to pick it: English styles that want body rather than dryness.',
    tags: ['british', 'english-ale', 'malty', 'fruity', 'mild', 'full-body'],
    url: 'https://brewingscience.com/product/british-ale-iii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-35',
    lab: 'Brewing Science Institute',
    productCode: 'A-35',
    name: 'British Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A classic British strain that finishes drier and crisper than most of its neighbors while still building a malty middle. Highly flocculent, and suggested for bitters and IPAs where English character should not smother the hops.',
    tags: ['british', 'english-ale', 'malty', 'dry', 'crisp', 'bitter', 'ipa'],
    url: 'https://brewingscience.com/product/british-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-68',
    lab: 'Brewing Science Institute',
    productCode: 'A-68',
    name: 'London Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The ESB strain: low attenuation and gentle, complex fruit esters that sit alongside English malt rather than over it, plus some diacetyl. It flocculates hard and can stall short, so confirm a stable final gravity before packaging and give it extra oxygen and rousing.',
    tags: ['esb', 'english-ale', 'london', 'bitter', 'mild', 'cask', 'diacetyl'],
    url: 'https://brewingscience.com/product/london-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-2',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-2',
    name: 'English Ale 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 63, max: 70 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "BSI's other listing of the classic ESB strain: mild complex fruit, some diacetyl, and low attenuation that flatters traditional English malt. Very flocculent and occasionally lazy, so check that gravity has genuinely stopped before you package.",
    tags: ['esb', 'english-ale', 'bitter', 'mild', 'cask', 'flocculent'],
    url: 'https://brewingscience.com/product/english-ale-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-69',
    lab: 'Brewing Science Institute',
    productCode: 'A-69',
    name: 'Timothy Taylor',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The Timothy Taylor strain, famous for balance: a full, chewy malt palate that still finishes dry, with nutty and stone-fruit esters. Reliably flocculent enough to give bright cask bitter, ESB and mild without filtration.',
    tags: ['english-ale', 'malty', 'nutty', 'stone-fruit', 'cask', 'bitter', 'esb'],
    url: 'https://brewingscience.com/product/timothy-taylor/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-75',
    lab: 'Brewing Science Institute',
    productCode: 'A-75',
    name: 'Henley on Thames Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Thames-valley brewery strain giving the textbook English fruit trio of apple, pear and honey over a light malt finish. A first choice for classic bitter, and equally at home in pale and amber ales on either side of the Atlantic.',
    tags: ['english-ale', 'apple', 'pear', 'honey', 'bitter', 'pale-ale', 'amber'],
    url: 'https://brewingscience.com/product/henley-on-thames-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-23',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-23',
    name: 'English Ale 7',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "The Henley-on-Thames strain under BSI's numbered listing: apple, pear and honey esters over a clean malt finish, aimed at bitters, ambers and pale ales.",
    tags: ['english-ale', 'apple', 'pear', 'honey', 'bitter', 'amber', 'pale-ale'],
    url: 'https://brewingscience.com/product/english-ale-7/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-78',
    lab: 'Brewing Science Institute',
    productCode: 'A-78',
    name: 'Scotch Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 73 },
    temperatureC: { min: 13, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An Edinburgh strain that ferments cleanly and tolerates 12% alcohol, which is precisely what a malty Scotch ale needs: the malt stays the subject and the yeast stays out of the way. Useful in any big beer, not only Scottish ones.',
    tags: ['scotch-ale', 'scottish', 'clean', 'high-gravity', 'malty'],
    url: 'https://brewingscience.com/product/scotch-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-28',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-28',
    name: 'Scottish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 14, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An Edinburgh strain with a soft, complex fruit profile that suits the shilling beers and scales up to strong Scotch ale and barley wine. Fermented at the cool end it turns cleaner and lighter.',
    tags: ['scottish', 'scotch-ale', 'fruity', 'barleywine', 'high-gravity'],
    url: 'https://brewingscience.com/product/scottish-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-84',
    lab: 'Brewing Science Institute',
    productCode: 'A-84',
    name: 'Irish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Clean, smooth and full-bodied, with esters that build as the temperature rises and a hint of diacetyl. BSI aim it at stout and porter, where the body it leaves supports the roast instead of thinning out under it.',
    tags: ['irish', 'stout', 'porter', 'full-body', 'smooth', 'amber'],
    url: 'https://brewingscience.com/product/irish-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-4',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-4',
    name: 'Irish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 74 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Dublin stout strain: clean-fermenting with a light fruitiness and a dry finish that lets roasted malt speak plainly. BSI note it also ferments cider.',
    tags: ['irish', 'dublin', 'stout', 'porter', 'dry', 'roast', 'cider'],
    url: 'https://brewingscience.com/product/irish-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-98',
    lab: 'Brewing Science Institute',
    productCode: 'A-98',
    name: 'British Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A well-attenuating British strain that finishes dry, crisp and faintly tart. It can be pushed down to 18 °C without complaint, and BSI recommend it for English bitter and for barley wine.',
    tags: ['british', 'english-ale', 'dry', 'crisp', 'tart', 'bitter', 'barleywine'],
    url: 'https://brewingscience.com/product/british-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-99',
    lab: 'Brewing Science Institute',
    productCode: 'A-99',
    name: 'Whitbread Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "The Whitbread strain: mildly malty with a light fruitiness, softer and rounder than BSI's A-98 British Ale. It clears well without filtration, which makes it a general-purpose English yeast across bitters, milds and browns.",
    tags: ['whitbread', 'english-ale', 'british', 'malty', 'fruity', 'flocculent'],
    url: 'https://brewingscience.com/product/whitbread-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-6',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-6',
    name: 'English Ale 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An English strain with a distinctive ester signature, high attenuation and high flocculation, which is the combination cask ale wants. Suits pale ale, bitter, mild and stout equally.',
    tags: ['english-ale', 'cask', 'estery', 'flocculent', 'bitter', 'stout'],
    url: 'https://brewingscience.com/product/english-ale-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-7',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-7',
    name: 'Dry English Ale 4',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Despite the English label this is a widely used American craft workhorse: clean esters, strong attenuation and a 12% ceiling, with flocculation so aggressive the yeast comes out in clumps. Good for IPA and pale ale as well as big stouts and porters.',
    tags: ['english-ale', 'dry', 'clean', 'ipa', 'pale-ale', 'high-gravity', 'flocculent'],
    url: 'https://brewingscience.com/product/dry-english-ale-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-22',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-22',
    name: 'English Ale 6',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 76 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A genuine top-cropping British strain with a flavorful, lightly fruity profile: the sort of yeast English bitter and mild were built around, and one you can still crop from the surface.',
    tags: ['english-ale', 'top-cropping', 'fruity', 'bitter', 'mild'],
    url: 'https://brewingscience.com/product/english-ale-6/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-25',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-25',
    name: 'English Ale 8',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An English strain that layers complex orchard-fruit and citrus esters over the malt. Some sulfur is possible during fermentation and conditions out. Aimed at bitters and pale ales.',
    tags: ['english-ale', 'citrus', 'fruity', 'bitter', 'pale-ale', 'sulphur'],
    url: 'https://brewingscience.com/product/english-ale-8/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-26',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-26',
    name: 'English Ale 9',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A mild, complex and consistently estery English strain with a narrow temperature window and a broad remit: BSI list everything from ESB to barley wine.',
    tags: ['english-ale', 'estery', 'esb', 'barleywine', 'versatile'],
    url: 'https://brewingscience.com/product/english-ale-9/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-99',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-99',
    name: 'Tom Hardy Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 83 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'An English strain kept for the beers nothing else will finish: with the right handling BSI say it will take a fermentation past 20% ABV, though the tolerance they publish is 15%. Fruit esters intensify as gravity rises, which suits imperial stout and very strong IPA.',
    tags: ['english-ale', 'high-gravity', 'imperial-stout', 'barleywine', 'fruity'],
    url: 'https://brewingscience.com/product/tom-hardy-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // German ale, kolsch and alt
  // -------------------------------------------------------------------------
  {
    id: 'bsi-a-07',
    lab: 'Brewing Science Institute',
    productCode: 'A-07',
    name: 'German Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 19 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      'A German top-cropping strain that finishes dry and crisp with a mild but layered profile: almost no diacetyl, few esters when kept cool, a little fruit if allowed to warm. BSI suggest it for alt and kolsch as well as American wheat and fruit ales.',
    tags: ['german-ale', 'kolsch', 'altbier', 'dry', 'crisp', 'clean', 'top-cropping'],
    url: 'https://brewingscience.com/product/german-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-38',
    lab: 'Brewing Science Institute',
    productCode: 'A-38',
    name: 'Old German Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 76 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "Shares a source with Wissenschaftliche Station 338. It builds a very full-bodied, complex, malt-driven beer and throws a dense rocky head in the fermenter. BSI's pick for American brown ale, and useful in wheat and fruit ales.",
    tags: ['german-ale', 'malty', 'full-body', 'brown-ale', 'wheat', 'top-cropping'],
    url: 'https://brewingscience.com/product/old-german-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-65',
    lab: 'Brewing Science Institute',
    productCode: 'A-65',
    name: 'Kolsch',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Koln top-cropping strain that builds real malt depth under a restrained fruitiness and finishes crisp: an ale that reads as a lager. Very little diacetyl, and an unusually wide temperature window for the style. Altbier suits it too.',
    tags: ['kolsch', 'hybrid', 'clean', 'crisp', 'malty', 'altbier', 'top-cropping'],
    url: 'https://brewingscience.com/product/kolsch-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-29',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-29',
    name: 'Kolsch',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A widely used Cologne kolsch strain that ferments clean and lager-like, but at warmer temperatures than most kolsch yeasts. Expect some sulfur that conditions away, and the variable flocculation the style is known for.',
    tags: ['kolsch', 'hybrid', 'clean', 'altbier', 'sulphur', 'warm-ferment'],
    url: 'https://brewingscience.com/product/kolsch/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-w177',
    lab: 'Brewing Science Institute',
    productCode: 'W177',
    name: 'Kolsch',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A classic kolsch strain with light fruit esters, low fusel production and strong diacetyl clean-up. Like the alt yeasts it works cool or warm; run it cool and it makes sulfur, which sharpens the mineral edge of the finished beer.',
    tags: ['kolsch', 'hybrid', 'clean', 'fruity', 'low-fusel', 'diacetyl-reduction'],
    url: 'https://brewingscience.com/product/kolsch-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-3',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-3',
    name: 'German Ale 1',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A clean German ale strain with slightly more ester than BSI-29 Kolsch, aimed at kolsch and altbier. Some sulfur during fermentation that conditions out.',
    tags: ['german-ale', 'kolsch', 'altbier', 'hybrid', 'clean', 'sulphur'],
    url: 'https://brewingscience.com/product/german-ale-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-11',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-11',
    name: 'German Ale 2',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A northern European strain that ferments clean and stops short, leaving a malty, full-bodied beer. The low attenuation is the reason to choose it: altbier and kolsch that need body rather than dryness.',
    tags: ['german-ale', 'altbier', 'kolsch', 'hybrid', 'malty', 'full-body'],
    url: 'https://brewingscience.com/product/german-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-335',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-335',
    name: 'German Alt',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 76, max: 78 },
    temperatureC: { min: 13, max: 19 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The original altbier strain from the German Hefebank: genuinely top-cropping, powdery, and slow to drop. It tolerates a wide temperature range, clean when cool and mildly fruity when warm.',
    tags: ['altbier', 'hybrid', 'top-cropping', 'powdery', 'german-ale'],
    url: 'https://brewingscience.com/product/german-alt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-36',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-36',
    name: 'Alt',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 13, max: 19 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "A Dusseldorf alt strain that finishes clean but leaves enough residual malt to soften altbier's hop bitterness. Low to medium flocculation keeps some yeast in suspension.",
    tags: ['altbier', 'dusseldorf', 'hybrid', 'malty', 'residual-sweetness'],
    url: 'https://brewingscience.com/product/alt/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Wheat
  // -------------------------------------------------------------------------
  {
    id: 'bsi-300',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-300',
    name: 'Hefeweizen 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The classic Bavarian weissbier strain, built around isoamyl acetate: banana leads and clove follows from the phenols. Pitch light and oxygenate lightly for more ester, or ferment cool with a ferulic acid rest for more clove. Low flocculation keeps the haze the style needs.',
    tags: ['hefeweizen', 'weissbier', 'banana', 'clove', 'wheat', 'phenolic'],
    url: 'https://brewingscience.com/product/hefeweizen-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-320',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-320',
    name: 'Hefeweizen 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An American hefeweizen strain, and the point is what it does not do: minimal esters and phenols leave a clean wheat beer, and low flocculation keeps it cloudy. A good base for fruited wheat ales where the fruit should lead.',
    tags: ['american-wheat', 'hefeweizen', 'clean', 'neutral', 'fruit-beer', 'wheat'],
    url: 'https://brewingscience.com/product/hefeweizen-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-351',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-351',
    name: 'Hefeweizen 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'A weissbier strain pitched toward spice rather than banana: moderately phenolic, with less isoamyl acetate than BSI-300 or W-68. Pitch rate and temperature move the balance a long way. BSI publish it as STA1 positive.',
    tags: ['hefeweizen', 'weissbier', 'clove', 'phenolic', 'spicy', 'wheat', 'diastatic'],
    url: 'https://brewingscience.com/product/hefeweizen-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-380',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-380',
    name: 'Hefeweizen 4',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "The spiciest of BSI's weizen strains: clove and nutmeg phenols dominate and banana barely registers. For hefeweizen at the dry, peppery end of the style.",
    tags: ['hefeweizen', 'clove', 'nutmeg', 'phenolic', 'spicy', 'wheat'],
    url: 'https://brewingscience.com/product/hefeweizen-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-w-33',
    lab: 'Brewing Science Institute',
    productCode: 'W-33',
    name: 'Weizen II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A German weizen strain that holds ester and phenol in balance rather than favouring either, giving a delicate, classic weissbier. It flocculates harder than most weizen yeasts, so it suits a brighter beer; some sulfur is possible.',
    tags: ['weizen', 'hefeweizen', 'balanced', 'banana', 'clove', 'flocculent'],
    url: 'https://brewingscience.com/product/weizen-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-w-38',
    lab: 'Brewing Science Institute',
    productCode: 'W-38',
    name: 'Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A German weizen strain whose ester production climbs with temperature: run it warm for a banana-forward weissbier, cool it for restraint.',
    tags: ['weizen', 'weissbier', 'banana', 'wheat'],
    url: 'https://brewingscience.com/product/weizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-w-68',
    lab: 'Brewing Science Institute',
    productCode: 'W-68',
    name: 'German Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The widely used Bavarian weizen strain: top-cropping, rich in clove, vanilla and banana, and unusually responsive to temperature, pitch rate and aeration, so that the same yeast will give three different beers. Weissbier, weizenbock, hefeweizen, and American wheat ales.',
    tags: ['weizen', 'hefeweizen', 'weizenbock', 'banana', 'clove', 'vanilla', 'top-cropping'],
    url: 'https://brewingscience.com/product/german-weizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-andechs-weizen',
    lab: 'Brewing Science Institute',
    productCode: 'Andechs Weizen',
    name: 'Andechs Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 74 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The weizen strain from the Andechs monastery brewery at Herrsching: a rich, spicy weissbier character with plenty of clove, vanilla and banana. Under-pitching and light oxygenation push the esters further. Weissbier, weizenbock and American wheat.',
    tags: ['weizen', 'hefeweizen', 'weizenbock', 'clove', 'banana', 'vanilla'],
    url: 'https://brewingscience.com/product/andechs-weizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-a-10',
    lab: 'Brewing Science Institute',
    productCode: 'A-10',
    name: 'American White Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Dusseldorf top-cropping strain, from Zum Uerige, that BSI sell for American wheat rather than alt. It ferments dry with a crisp, faintly tart edge and very little ester, which suits American hefeweizen and fruited wheat ales.',
    tags: ['american-wheat', 'dusseldorf', 'dry', 'tart', 'crisp', 'fruit-beer'],
    url: 'https://brewingscience.com/product/american-white-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-cl980',
    lab: 'Brewing Science Institute',
    productCode: 'CL980',
    name: 'American White Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 76 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An American wheat strain that builds an exceptionally round, clean malt palate with almost no ester of its own: the yeast an unfiltered American wheat beer is supposed to have. Also useful for fruited wheats and American-style altbier.',
    tags: ['american-wheat', 'clean', 'malty', 'neutral', 'fruit-beer', 'altbier'],
    url: 'https://brewingscience.com/product/american-white-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Belgian ale
  // -------------------------------------------------------------------------
  {
    id: 'bsi-abs3',
    lab: 'Brewing Science Institute',
    productCode: 'ABS3',
    name: 'Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A phenol-forward Belgian strain with only mild fruit, leaving a complex, spicy beer and tolerating 12% alcohol. Chosen where the phenols are the point rather than the esters.',
    tags: ['belgian', 'phenolic', 'spicy', 'high-gravity'],
    url: 'https://brewingscience.com/product/abs3-belgian-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-22',
    lab: 'Brewing Science Institute',
    productCode: 'B-22',
    name: 'LaChouffe',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 29 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "The La Chouffe strain, and like BSI's ABS3 a phenol-forward Belgian with restrained fruit and a high alcohol ceiling. Its published range runs to 29 °C, which is where the spice really develops.",
    tags: ['belgian', 'phenolic', 'spicy', 'high-gravity', 'warm-ferment'],
    url: 'https://brewingscience.com/product/lachouffe/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-550',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-550',
    name: 'Belgian Ale 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "Another of BSI's phenol-forward Belgians: prominent spice, mild fruit, a 12% ceiling, and a warm temperature range that starts where most ale strains stop.",
    tags: ['belgian', 'phenolic', 'spicy', 'warm-ferment', 'high-gravity'],
    url: 'https://brewingscience.com/product/belgian-ale-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-14',
    lab: 'Brewing Science Institute',
    productCode: 'B-14',
    name: 'Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A popular abbey strain from a Belgian monastery, giving ripe fruit and a bubblegum ester note that can be reined in by fermenting cooler. Heavy ester production and a 12% ceiling make it a standard for dubbel, tripel and quad.',
    tags: ['belgian', 'abbey', 'trappist', 'dubbel', 'tripel', 'quad', 'bubblegum', 'fruity'],
    url: 'https://brewingscience.com/product/belgian-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-500',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-500',
    name: 'Trappist Ale 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Trappist monastery strain giving ripe fruit and bubblegum esters that subside if fermented cool. High tolerance and strong ester production suit dubbel, tripel and quadrupel.',
    tags: ['belgian', 'trappist', 'abbey', 'dubbel', 'tripel', 'quad', 'fruity'],
    url: 'https://brewingscience.com/product/trappist-ale-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-42',
    lab: 'Brewing Science Institute',
    productCode: 'B-42',
    name: 'Essens Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A distinctive Belgian strain with clear apple and plum esters and very little phenol, finishing dry and slightly tart at up to 12% alcohol. Witbier, Belgian white IPA and tripel.',
    tags: ['belgian', 'witbier', 'apple', 'plum', 'dry', 'tart', 'tripel', 'wheat'],
    url: 'https://brewingscience.com/product/essens-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-44',
    lab: 'Brewing Science Institute',
    productCode: 'B-44',
    name: 'Celis',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Celis witbier strain: tart, lightly phenolic, and a heavy producer of organic acids, which is where the dry finish comes from. Beyond witbier and grand cru it suits American fruited wheats, white IPA and pale ale.',
    tags: ['witbier', 'belgian', 'tart', 'phenolic', 'wheat', 'grand-cru'],
    url: 'https://brewingscience.com/product/celis/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-400',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-400',
    name: 'Belgian White Ale 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "BSI's numbered listing of the classic Belgian witbier yeast: tart, mildly phenolic, and acid-producing enough to finish dry. Witbier and grand cru first, then American fruited wheat and white IPA.",
    tags: ['witbier', 'belgian', 'tart', 'phenolic', 'wheat', 'grand-cru'],
    url: 'https://brewingscience.com/product/belgian-white-ale-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-410',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-410',
    name: 'Belgian White Ale 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A wit strain with more spice and phenol than BSI-400 and lower attenuation, so it finishes tart and dry but with more sugar left behind. For witbier and table beer with some character to them.',
    tags: ['witbier', 'belgian', 'phenolic', 'spicy', 'tart', 'table-beer'],
    url: 'https://brewingscience.com/product/belgian-white-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-62',
    lab: 'Brewing Science Institute',
    productCode: 'B-62',
    name: 'Belgian Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Rochefort strain, and it tastes like one: a distinct solvent note from vigorous ethanol production, light fruit, and a dry finish. Belgian dark strong and brown ales, and American barley wine.',
    tags: ['belgian', 'dark-strong', 'solvent', 'dry', 'barleywine'],
    url: 'https://brewingscience.com/product/belgian-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-63',
    lab: 'Brewing Science Institute',
    productCode: 'B-63',
    name: 'Classic Belgian',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Belgian house strain for wit and grand cru: phenolic in character with the fruit held back, and a low flocculation that keeps the beer hazy.',
    tags: ['belgian', 'witbier', 'grand-cru', 'phenolic'],
    url: 'https://brewingscience.com/product/classic-belgian/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-64',
    lab: 'Brewing Science Institute',
    productCode: 'B-64',
    name: 'Canadian / Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 79 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Mild phenols that grow with temperature, low esters, and a dry, faintly tart finish: a Belgian strain that stays balanced instead of shouting. A 12% ceiling and a range up to 27 °C make it broadly useful across Belgian styles.',
    tags: ['belgian', 'phenolic', 'dry', 'tart', 'balanced', 'warm-ferment'],
    url: 'https://brewingscience.com/product/canadian-belgian-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-82',
    lab: 'Brewing Science Institute',
    productCode: 'B-82',
    name: 'Belgian Sour Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Tart without any bacteria: this Belgian strain throws a large quantity of organic acids, but not lactic acid, so it tastes sharp while remaining a clean single-culture fermentation. Spicy and phenolic on the nose, very dry on the palate, and a useful corrective to the sweetness of a quad.',
    tags: ['belgian', 'tart', 'acidic', 'phenolic', 'dry', 'quad'],
    url: 'https://brewingscience.com/product/belgian-sour-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-87',
    lab: 'Brewing Science Institute',
    productCode: 'B-87',
    name: 'Trappist Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A robust Trappist strain, close to B-14 but less fruity: a rich ester profile with ripe fruit and some phenol over a malty palate, finishing dry. High attenuation and tolerance suit biere de garde, dubbel and tripel, and it can be top-cropped. BSI publish it as STA1 positive.',
    tags: [
      'trappist',
      'belgian',
      'biere-de-garde',
      'dubbel',
      'tripel',
      'top-cropping',
      'diastatic',
    ],
    url: 'https://brewingscience.com/product/trappist-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-530',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-530',
    name: 'Trappist Ale 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Trappist monastery strain that ferments dry with a distinct ester profile and some phenolic edge over a malty middle. High attenuation and tolerance point it at biere de garde, dubbel and tripel, and it top-crops well. STA1 positive.',
    tags: [
      'trappist',
      'belgian',
      'biere-de-garde',
      'dubbel',
      'tripel',
      'top-cropping',
      'diastatic',
    ],
    url: 'https://brewingscience.com/product/trappist-ale-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-510',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-510',
    name: 'Trappist Ale 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A high-gravity Trappist strain that finishes dry with a slight tartness, chosen for the 15% ceiling as much as for any particular flavor. Dubbel, tripel and Belgian strong ales.',
    tags: ['trappist', 'belgian', 'high-gravity', 'dry', 'tart', 'dubbel', 'tripel'],
    url: 'https://brewingscience.com/product/trappist-ale-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-540',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-540',
    name: 'Trappist Ale 4',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Trappist strain that pushes alcohol hard enough to leave a characteristic solvent note alongside medium fruit, sitting between BSI-500 and BSI-530 on ester intensity. For Belgian dark strong and specialty beers at high gravity. STA1 positive.',
    tags: ['trappist', 'belgian', 'high-gravity', 'dark-strong', 'solvent', 'fruity', 'diastatic'],
    url: 'https://brewingscience.com/product/trappist-ale-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-b-88',
    lab: 'Brewing Science Institute',
    productCode: 'B-88',
    name: 'Breendonk Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 13,
    sta1: true,
    description:
      "A robust Belgian strain with a fruity nose and palate and a dry, tart finish, holding to 13% alcohol. BSI's first choice for Belgian golden strong ale, and published as STA1 positive.",
    tags: ['belgian', 'golden-strong', 'fruity', 'dry', 'tart', 'high-gravity', 'diastatic'],
    url: 'https://brewingscience.com/product/breendonk-belgian-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-570',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-570',
    name: 'Belgian Ale 4',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'Rich fruit and phenol on both nose and palate, a dry finish, and a 15% ceiling. A Belgian golden strong strain, with the low flocculation that style tolerates. STA1 positive.',
    tags: ['belgian', 'golden-strong', 'fruity', 'phenolic', 'high-gravity', 'dry', 'diastatic'],
    url: 'https://brewingscience.com/product/belgian-ale-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-515',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-515',
    name: 'Belgian Ale 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The cleanest Belgian strain BSI offer, nearly lager-like, with only a trace of sulfur and enough ester restraint that hops come through undisturbed. Belgian pale ale and Belgian IPA.',
    tags: ['belgian', 'clean', 'neutral', 'belgian-ipa', 'pale-ale'],
    url: 'https://brewingscience.com/product/belgian-ale-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-575',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-575',
    name: 'Belgian Ale Blend 5',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A blend of a Trappist strain with a Belgian one, combining high attenuation with a 12% ceiling. Meant for strong Belgian beers where a single culture would stall or flatten out.',
    tags: ['belgian', 'blend', 'trappist', 'high-gravity', 'attenuative'],
    url: 'https://brewingscience.com/product/belgian-ale-blend-5/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Saison and farmhouse
  // -------------------------------------------------------------------------
  {
    id: 'bsi-565',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-565',
    name: 'Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 75 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'The Wallonian saison strain, with all its virtues and its one famous vice: earthy pepper and spice with a bubblegum edge, tart and dry, and a fermentation that starts violently then stalls well short of terminal. Let it free-rise and give it time; for strong beers, blend it with S-26. STA1 positive.',
    tags: ['saison', 'farmhouse', 'peppery', 'spicy', 'earthy', 'diastatic', 'warm-ferment'],
    url: 'https://brewingscience.com/product/saison/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-s-24',
    lab: 'Brewing Science Institute',
    productCode: 'S-24',
    name: 'Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 75 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      "BSI's second listing of the classic farmhouse saison strain: earthy, peppery and spicy with a touch of bubblegum, tart and dry on the finish, and published here with lower flocculation than BSI-565. Same caution too, since it charges out of the gate and then stops short. STA1 positive.",
    tags: ['saison', 'farmhouse', 'peppery', 'spicy', 'earthy', 'diastatic', 'warm-ferment'],
    url: 'https://brewingscience.com/product/saison-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-566',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-566',
    name: 'Saison 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      "A saison strain that trades some of BSI-565's spice for fruit esters, with moderate clove, and, more usefully, ferments faster and actually finishes. Blends well with other saison and Belgian strains. STA1 positive.",
    tags: ['saison', 'farmhouse', 'fruity', 'clove', 'attenuative', 'diastatic'],
    url: 'https://brewingscience.com/product/saison-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-s-11',
    lab: 'Brewing Science Institute',
    productCode: 'S-11',
    name: 'French Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A French strain that makes intensely aromatic saison: clean citrus esters, pepper and spice, with none of the earthiness and very little phenol. Extremely attenuative, yet it somehow leaves a silky mouthfeel behind in a very dry beer, and it amplifies spice and aroma hop additions. STA1 positive.',
    tags: ['saison', 'french', 'farmhouse', 'citrus', 'peppery', 'attenuative', 'diastatic'],
    url: 'https://brewingscience.com/product/french-saison/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-s-25',
    lab: 'Brewing Science Institute',
    productCode: 'S-25',
    name: 'Bier de Garde',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A French-Belgian strain for strong farmhouse beers: light to moderate esters, subtle spice, and a malty, initially sweet palate that dries out to a slightly tart finish. It ferments without hesitation and tolerates a lot of alcohol. STA1 positive.',
    tags: [
      'biere-de-garde',
      'french',
      'belgian',
      'farmhouse',
      'malty',
      'spicy',
      'high-gravity',
      'diastatic',
    ],
    url: 'https://brewingscience.com/product/bier-de-garde/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-s-26',
    lab: 'Brewing Science Institute',
    productCode: 'S-26',
    name: 'Farmhouse Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      "A farmhouse strain with complex esters over earth and pepper, tart and dry on the finish. Very high attenuation and tolerance, which is why BSI recommend blending it into stalled saisons such as S-24. BSI publish flocculation as 'variable', so the value here is ours. STA1 positive.",
    tags: ['saison', 'farmhouse', 'peppery', 'earthy', 'dry', 'tart', 'blending', 'diastatic'],
    url: 'https://brewingscience.com/product/farmhouse-ale/',
    provenance: 'representative',
  },
  {
    id: 'bsi-72',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-72',
    name: 'French Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A clean, malty French strain with a low ester profile below 21 °C that opens up considerably if pushed above its published range. Very little diacetyl. Biere de garde and French farmhouse styles.',
    tags: ['french', 'biere-de-garde', 'farmhouse', 'clean', 'malty', 'low-diacetyl'],
    url: 'https://brewingscience.com/product/french-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Kveik
  // -------------------------------------------------------------------------
  {
    id: 'bsi-hornindal-kveik',
    lab: 'Brewing Science Institute',
    productCode: 'Hornindal Kveik',
    name: 'Hornindal Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 80, max: 85 },
    temperatureC: { min: 22, max: 37 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: false,
    description:
      'The Hornindal farmhouse strain from Grodas: very fast, very clean, and comfortable above 35 °C, producing tropical and fruity esters with no phenol, fusel or sulfur to speak of. Made for hoppy American styles brewed on a short turnaround.',
    tags: ['kveik', 'norwegian', 'hornindal', 'tropical', 'fruity', 'fast', 'ipa', 'warm-ferment'],
    url: 'https://brewingscience.com/product/hornindal-kveik/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-nornes-kveik',
    lab: 'Brewing Science Institute',
    productCode: 'Nornes Kveik',
    name: 'Nornes Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 80, max: 85 },
    temperatureC: { min: 22, max: 37 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'A traditional kveik from the Nornes farmstead, fruity with a floral edge and happy to ferment past 35 °C. Good for IPA and pale ale as well as the farmhouse beers it came from.',
    tags: ['kveik', 'norwegian', 'nornes', 'fruity', 'floral', 'farmhouse', 'ipa', 'warm-ferment'],
    url: 'https://brewingscience.com/product/nornes-kveik/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-voss-kveik',
    lab: 'Brewing Science Institute',
    productCode: 'Voss Kveik',
    name: 'Voss Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 20, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: false,
    description:
      'The Voss kveik: bright citrus fruit that suits citrus-forward American hops, and clean across most of a very wide temperature range. Push it past 35 °C and the character intensifies with a faint spice. IPA and pale ale.',
    tags: ['kveik', 'norwegian', 'voss', 'citrus', 'clean', 'ipa', 'pale-ale', 'warm-ferment'],
    url: 'https://brewingscience.com/product/voss-kveik/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-akershus-kveik',
    lab: 'Brewing Science Institute',
    productCode: 'Akershus Kveik',
    name: 'Akershus Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 29, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A kveik from the country around Oslo that ferments fast and remarkably clean, close to a lager profile in ale time. At the top of its range it turns fruity with floral notes. The kveik to reach for when speed matters more than farmhouse character.',
    tags: ['kveik', 'norwegian', 'akershus', 'clean', 'fast', 'lager-like', 'versatile'],
    url: 'https://brewingscience.com/product/akershus-kveik/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Lager
  // -------------------------------------------------------------------------
  {
    id: 'bsi-3470',
    lab: 'Brewing Science Institute',
    productCode: '3470',
    name: 'German Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 8, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: false,
    description:
      "BSI's listing of 34/70, the most widely used lager strain in the world: clean and crisp, leaving just enough malt behind to carry a pilsner's hops. The obvious house lager yeast, with bock, pilsner and Oktoberfest all inside its range.",
    tags: ['lager', 'german', 'clean', 'crisp', 'pilsner', 'bock', 'oktoberfest', 'house-strain'],
    url: 'https://brewingscience.com/product/3470-german-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-830',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-830',
    name: 'German Lager 2',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 9, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      "BSI's numbered listing of the same 34/70 lager strain: clean and crisp with residual maltiness, and versatile enough to serve as a brewery's only lager yeast.",
    tags: ['lager', 'german', 'clean', 'crisp', 'pilsner', 'bock', 'oktoberfest'],
    url: 'https://brewingscience.com/product/german-lager-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-24',
    lab: 'Brewing Science Institute',
    productCode: 'L-24',
    name: 'Czech 34/70 Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The 34/70 lager strain again, this time sourced from the Saaz region of the Czech Republic. Clean and crisp with residual malt, and a capable all-purpose lager for bock, pilsner and Oktoberfest.',
    tags: ['lager', 'czech', 'saaz', 'clean', 'crisp', 'pilsner', 'bock', 'oktoberfest'],
    url: 'https://brewingscience.com/product/czech-34-70-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-800',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-800',
    name: 'Czech Lager 1',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 9, max: 16 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The original pilsner lager strain: clean and crisp, dry but malty. It produces sulfur during fermentation that conditions away. For authentic Czech pilsner.',
    tags: ['lager', 'czech', 'pilsner', 'clean', 'crisp', 'sulphur'],
    url: 'https://brewingscience.com/product/czech-lager-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-78',
    lab: 'Brewing Science Institute',
    productCode: 'L-78',
    name: 'Original Pils Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      "BSI's other listing of the original pilsner strain from the home of the style: clean and crisp with a dry, malty finish, and sulfur during fermentation that conditions out.",
    tags: ['lager', 'pilsner', 'czech', 'clean', 'dry', 'malty', 'sulphur'],
    url: 'https://brewingscience.com/product/original-pils-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-802',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-802',
    name: 'Czech Lager 2',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Czech pilsner strain that ferments clean with low diacetyl and attenuates further than BSI-800, leaving a dry, crisp body that puts hop bitterness in the foreground. Bohemian pilsner.',
    tags: ['lager', 'czech', 'pilsner', 'dry', 'crisp', 'attenuative', 'low-diacetyl'],
    url: 'https://brewingscience.com/product/czech-lager-2/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-820',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-820',
    name: 'German Lager 1',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 65, max: 73 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "A classic German lager strain sharing its source with BSI's 206 listing: rich, malty, full-bodied and clean. Dunkel and export first, then Munchner, bock and doppelbock.",
    tags: ['lager', 'german', 'malty', 'full-body', 'dunkel', 'bock', 'doppelbock', 'export'],
    url: 'https://brewingscience.com/product/german-lager-1/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-06',
    lab: 'Brewing Science Institute',
    productCode: 'L-06',
    name: 'German 206 Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The 206 German lager strain: rich, malty and full-bodied while staying clean. Dunkel and export styles, and equally at home in Munchner, bock and doppelbock.',
    tags: ['lager', 'german', 'malty', 'full-body', 'dunkel', 'bock', 'doppelbock'],
    url: 'https://brewingscience.com/product/german-206-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-833',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-833',
    name: 'German Lager 3',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 9, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A southern Bavarian lager strain that balances malt against hop rather than favouring either. Best in the darker German beers, bock, doppelbock and Oktoberfest, though it will make a malty helles too.',
    tags: [
      'lager',
      'german',
      'bavarian',
      'balanced',
      'bock',
      'doppelbock',
      'oktoberfest',
      'helles',
    ],
    url: 'https://brewingscience.com/product/german-lager-3/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-838',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-838',
    name: 'German Lager 4',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 68, max: 76 },
    temperatureC: { min: 9, max: 17 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A robust general-purpose German lager strain that ferments and drops reliably and leaves a malty, balanced beer. Expect sulfur and some diacetyl, so plan a diacetyl rest and proper conditioning.',
    tags: ['lager', 'german', 'malty', 'balanced', 'diacetyl-rest', 'sulphur'],
    url: 'https://brewingscience.com/product/german-lager-4/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-920',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-920',
    name: 'German Lager 5',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 66, max: 73 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Bavarian lager strain that deliberately stops short: low attenuation leaves a malty beer with a full mouthfeel and a small but present ester profile. For bock, Oktoberfest and dark lagers.',
    tags: [
      'lager',
      'german',
      'bavarian',
      'malty',
      'full-body',
      'bock',
      'oktoberfest',
      'dark-lager',
    ],
    url: 'https://brewingscience.com/product/german-lager-5/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-08',
    lab: 'Brewing Science Institute',
    productCode: 'L-08',
    name: 'German 308 Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The 308 strain, sharing a source with Wissenschaftliche Station 308. Smooth, soft and full-bodied in the malt, though BSI warn it can be temperamental. Their first choice for Munchner and second for Oktoberfest.',
    tags: ['lager', 'german', 'malty', 'full-body', 'munich', 'oktoberfest'],
    url: 'https://brewingscience.com/product/german-308-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-33',
    lab: 'Brewing Science Institute',
    productCode: 'L-33',
    name: 'Oktoberfest Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The Oktoberfest strain: rich, malty and complex, attenuating well while leaving real body and mouthfeel behind, and low in sulfur. Style-specific in the best sense.',
    tags: ['lager', 'oktoberfest', 'festbier', 'malty', 'full-body', 'german'],
    url: 'https://brewingscience.com/product/oktoberfest-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-950',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-950',
    name: 'Bavarian Pilsner',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 78, max: 80 },
    temperatureC: { min: 9, max: 10 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A lower-Bavarian pilsner strain with high attenuation and low diacetyl, and a narrow cold window it likes to be held in. German pilsner and pale lager.',
    tags: ['lager', 'pilsner', 'bavarian', 'german', 'attenuative', 'low-diacetyl'],
    url: 'https://brewingscience.com/product/bavarian-pilsner-bsi-950/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-960',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-960',
    name: 'Bavarian Bock',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 80, max: 83 },
    temperatureC: { min: 11, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Bavarian lager strain that ferments strongly and finishes very dry for a lager, with low diacetyl, light esters and a touch of sulfur. BSI rate it to 16–17 °P, which is bock and doppelbock territory.',
    tags: ['lager', 'bock', 'doppelbock', 'bavarian', 'attenuative', 'high-gravity', 'sulphur'],
    url: 'https://brewingscience.com/product/bsi-960-bavarian-bock/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-970',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-970',
    name: 'Bavarian Helles',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 79, max: 81 },
    temperatureC: { min: 9, max: 10 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Bavarian lager strain for helles: rich and malty, with more sulfur than most, which is part of the Bayerisch Hell character rather than a fault.',
    tags: ['lager', 'helles', 'bavarian', 'malty', 'sulphur', 'german'],
    url: 'https://brewingscience.com/product/bsi-970-bavarian-helles/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-980',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-980',
    name: 'Bavarian Dunkel',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 78, max: 80 },
    temperatureC: { min: 9, max: 10 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Bavarian lager strain that stays clean and neutral so the dark malt carries the beer. Low diacetyl and sulfur, though higher alcohols are possible. Dunkel and Bavarian dark lager.',
    tags: ['lager', 'dunkel', 'bavarian', 'clean', 'neutral', 'dark-lager', 'german'],
    url: 'https://brewingscience.com/product/bsi-980-bavarian-dunkel/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-andechs-lager',
    lab: 'Brewing Science Institute',
    productCode: 'Andechs Lager',
    name: 'Andechs Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 74 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The lager strain from the Andechs monastery brewery near Herrsching: well-rounded and malty, with low ester and sulfur production and a narrow cold band. Helles, dunkel, bock and Oktoberfest.',
    tags: ['lager', 'german', 'malty', 'clean', 'helles', 'dunkel', 'oktoberfest', 'bock'],
    url: 'https://brewingscience.com/product/andechs-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-augustiner',
    lab: 'Brewing Science Institute',
    productCode: 'Augustiner',
    name: 'Augustiner',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 74 },
    temperatureC: { min: 11, max: 17 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Augustiner strain from Munich: clean, richly malty, low in diacetyl and sulfur, and willing to ferment a little warmer than most lager yeasts. Helles first, but it covers everything from pilsner to dunkel.',
    tags: ['lager', 'munich', 'german', 'malty', 'clean', 'helles', 'versatile'],
    url: 'https://brewingscience.com/product/augustiner/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-ettal-lager',
    lab: 'Brewing Science Institute',
    productCode: 'Ettal Lager',
    name: 'Ettal Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 78 },
    temperatureC: { min: 8, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A historic lager strain from Kloster Ettal in Bavaria that ferments clean but leaves a pronounced malt character. Helles, dunkel, Marzen, Hellerbock and doppelbock.',
    tags: ['lager', 'bavarian', 'malty', 'clean', 'helles', 'dunkel', 'marzen', 'doppelbock'],
    url: 'https://brewingscience.com/product/ettal-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-weltenburg-lager',
    lab: 'Brewing Science Institute',
    productCode: 'Weltenburg Lager',
    name: 'Weltenburg Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 78 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A historic lager strain from Kloster Weltenburg, the Benedictine monastery brewery in Bavaria. Rich and malty, with dunkel and doppelbock as its home styles.',
    tags: ['lager', 'bavarian', 'malty', 'dunkel', 'doppelbock', 'german'],
    url: 'https://brewingscience.com/product/weltenburg-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-840',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-840',
    name: 'American Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The American macro lager strain: dry and clean, minimal sulfur and diacetyl, with a slight tart apple note. Exactly what an American light lager is supposed to taste of.',
    tags: ['lager', 'american', 'clean', 'dry', 'light-lager'],
    url: 'https://brewingscience.com/product/american-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-07',
    lab: 'Brewing Science Institute',
    productCode: 'L-07',
    name: 'American Megabrewery Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      "BSI's other listing of the American macro lager strain: dry, crisp and clean with very little sulfur or diacetyl, and a faint tart apple fruitiness.",
    tags: ['lager', 'american', 'dry', 'crisp', 'clean', 'light-lager'],
    url: 'https://brewingscience.com/product/american-megabrewery-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-05',
    lab: 'Brewing Science Institute',
    productCode: 'L-05',
    name: 'Colorado Mountain Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      "BSI's home-state strain: a versatile lager yeast with light esters and a mild malty palate that gives it a faintly Bavarian character next to the other American macro strains. North American lager and light pilsner.",
    tags: ['lager', 'american', 'colorado', 'malty', 'balanced', 'light-lager', 'pilsner'],
    url: 'https://brewingscience.com/product/l-05-colorado-mountain-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-35',
    lab: 'Brewing Science Institute',
    productCode: 'L-35',
    name: 'August Schell Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'A historic American lager strain with a bold, complex, woody character quite unlike the modern macro strains. Some diacetyl is possible. For pre-Prohibition American lager.',
    tags: ['lager', 'american', 'historic', 'woody', 'complex'],
    url: 'https://brewingscience.com/product/august-schell-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-42',
    lab: 'Brewing Science Institute',
    productCode: 'L-42',
    name: 'Denmark Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Danish lager strain with a soft, light profile that steps back and lets hop character through. Useful across American and German lager styles, with low flocculation.',
    tags: ['lager', 'danish', 'soft', 'hop-forward', 'clean'],
    url: 'https://brewingscience.com/product/denmark-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-47',
    lab: 'Brewing Science Institute',
    productCode: 'L-47',
    name: 'Denmark Lager II',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Danish lager strain that finishes clean and dry with a little sulfur, often chosen for aggressively hopped pilsner where the yeast has to stay out of the way.',
    tags: ['lager', 'danish', 'dry', 'clean', 'pilsner', 'sulphur'],
    url: 'https://brewingscience.com/product/denmark-lager-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-72',
    lab: 'Brewing Science Institute',
    productCode: 'L-72',
    name: 'Christian Schmidt Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'The strain from the old Christian Schmidt brewery in Philadelphia: malty and clean on the finish, traditionally used for American pilsner and light lager.',
    tags: ['lager', 'american', 'historic', 'malty', 'clean', 'pilsner', 'light-lager'],
    url: 'https://brewingscience.com/product/christian-schmidt-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-940',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-940',
    name: 'Mexican Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 78 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The strain from a Mexico City brewery, aimed squarely at crisp light cerveza: clean fermentation, balanced malt and hop, and very drinkable.',
    tags: ['lager', 'mexican', 'clean', 'crisp', 'light-lager'],
    url: 'https://brewingscience.com/product/mexican-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-810',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-810',
    name: 'San Francisco Lager',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 14, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The California common strain: a lager yeast that keeps its lager character up to about 20 °C, ferments quickly and drops bright. Malty with a whisper of fruit, and the obvious choice for steam beer.',
    tags: ['california-common', 'steam-beer', 'lager', 'hybrid', 'warm-ferment', 'malty'],
    url: 'https://brewingscience.com/product/san-francisco-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-l-12',
    lab: 'Brewing Science Institute',
    productCode: 'L-12',
    name: 'Common Lager',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 14, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      "BSI's second listing of the San Francisco steam-beer strain, under their lager numbering and with a higher published attenuation than BSI-810. Ferments warm and fast for a lager yeast and flocculates hard enough to give a brilliantly clear California common.",
    tags: ['california-common', 'steam-beer', 'lager', 'hybrid', 'warm-ferment', 'clean'],
    url: 'https://brewingscience.com/product/common-lager/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Champagne and seltzer
  // -------------------------------------------------------------------------
  {
    id: 'bsi-1868',
    lab: 'Brewing Science Institute',
    productCode: '1868',
    name: 'Pasteur Champagne',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 68 },
    temperatureC: { min: 4, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 18,
    sta1: false,
    description:
      'The most widely used champagne yeast there is: fast, neutral, with an enormous temperature range and an 18% ceiling. Brut IPA, cider, seltzer and champagne, plus the two jobs it is really kept for, restarting stuck fermentations and bottle conditioning.',
    tags: ['champagne', 'wine', 'cider', 'seltzer', 'brut-ipa', 'bottle-conditioning', 'neutral'],
    url: 'https://brewingscience.com/product/1868-pasteur-champagne/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-715',
    lab: 'Brewing Science Institute',
    productCode: 'BSI-715',
    name: 'Champagne',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 7, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A champagne strain for high-alcohol beers and bottle conditioning, with a very wide temperature range and a 15% ceiling. Also used in brut IPA, cider and seltzer.',
    tags: ['champagne', 'bottle-conditioning', 'high-gravity', 'cider', 'seltzer', 'brut-ipa'],
    url: 'https://brewingscience.com/product/champagne/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-louis-p',
    lab: 'Brewing Science Institute',
    productCode: 'Louis P',
    name: 'Louis P Hard Seltzer Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 16, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 18,
    sta1: false,
    description:
      "A champagne variant sold for hard seltzer, finishing dry, crisp and light at up to 18% alcohol. Sugar washes need help, so BSI recommend DAP and nutrient plus a generous pitch to suppress growth and ester formation. Attenuation is published only as 'high', so the band here is ours.",
    tags: ['seltzer', 'champagne', 'dry', 'crisp', 'neutral', 'high-gravity'],
    url: 'https://brewingscience.com/product/louis-p-hard-seltzer-yeast/',
    provenance: 'representative',
  },
  // -------------------------------------------------------------------------
  // Wild cultures and bacteria
  // -------------------------------------------------------------------------
  {
    id: 'bsi-b-73',
    lab: 'Brewing Science Institute',
    productCode: 'B-73',
    name: 'Roselare Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 82 },
    temperatureC: { min: 18, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: true,
    description:
      "A blend of lambic cultures for West Flanders red: earthy and complex with a pie-cherry sourness that needs up to eighteen months to arrive, and mixed cultures attenuative enough to leave the beer very dry. It ships with a lactobacillus; pediococcus can be added on request. BSI publish flocculation as 'variable', so the value here is ours. STA1 positive.",
    tags: [
      'mixed-culture',
      'sour',
      'flanders-red',
      'lambic',
      'brett',
      'lactobacillus',
      'blend',
      'diastatic',
    ],
    url: 'https://brewingscience.com/product/roselare-blend/',
    provenance: 'representative',
  },
  {
    id: 'bsi-brettanomyces-bruxellensis',
    lab: 'Brewing Science Institute',
    productCode: 'B. bruxellensis',
    name: 'Brettanomyces bruxellensis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 82 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A classic Brettanomyces bruxellensis: medium-intensity funk with earth and mild fruit esters, meant for secondary fermentation in Belgian and lambic-style beers. The character takes months rather than weeks. Blends well with Belgian and saison strains, and with pediococcus if lactic acid is wanted.',
    tags: ['brett', 'brettanomyces', 'wild', 'funk', 'earthy', 'secondary-fermentation', 'lambic'],
    url: 'https://brewingscience.com/product/brettanomyces-bruxellensis/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-brettanomyces-clausenii',
    lab: 'Brewing Science Institute',
    productCode: 'B. clausenii',
    name: 'Brettanomyces clausenii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 89, max: 90 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "The gentlest of BSI's Brettanomyces: low-intensity funk, subdued fruit and a slight earthiness, usable in primary as well as secondary fermentation. It attenuates hard, published at 89–90%, so expect a very dry beer.",
    tags: ['brett', 'brettanomyces', 'wild', 'fruity', 'attenuative', 'primary-fermentation'],
    url: 'https://brewingscience.com/product/brettanomyces-clausenii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-brettanomyces-bruxellensis-drei',
    lab: 'Brewing Science Institute',
    productCode: 'B. bruxellensis Drei',
    name: 'Brettanomyces bruxellensis var. Drei',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 82 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Drei variant of Brettanomyces bruxellensis, and the most aromatic culture BSI list: complex earth with a fruit character they compare to sweet tarts, and noticeably fruitier than plain bruxellensis. For secondary fermentation in lambic-style beers.',
    tags: ['brett', 'brettanomyces', 'wild', 'fruity', 'earthy', 'aromatic', 'lambic'],
    url: 'https://brewingscience.com/product/brettanomyces-bruxellensis-var-drei/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-brettanomyces-lambicus',
    lab: 'Brewing Science Institute',
    productCode: 'B. lambicus',
    name: 'Brettanomyces lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 73, max: 74 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "Brettanomyces lambicus at full strength, from a Trappist brewery in the Gaume: the horse-blanket end of the funk spectrum and the slowest of BSI's Bretts to show its hand. A classic secondary culture for Belgian and lambic-style beers.",
    tags: ['brett', 'brettanomyces', 'wild', 'funk', 'lambic', 'secondary-fermentation'],
    url: 'https://brewingscience.com/product/brettanomyces-lambicus/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bsi-lactobacillus-brevis',
    lab: 'Brewing Science Institute',
    productCode: 'L. brevis',
    name: 'Lactobacillus brevis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 39, max: 41 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A heterofermentative Lactobacillus with more hop tolerance than L. delbrueckii, which makes it the choice when there are already IBUs in the wort: kettle souring or barrel ageing. BSI publish a temperature range but no attenuation or flocculation, and the attenuation figure here is nominal because it makes acid, not alcohol.',
    tags: ['lactobacillus', 'sour', 'kettle-sour', 'bacteria', 'barrel-aged'],
    url: 'https://brewingscience.com/product/lactobacillus-brevis/',
    provenance: 'representative',
  },
  {
    id: 'bsi-lactobacillus-delbrueckii',
    lab: 'Brewing Science Institute',
    productCode: 'L. delbrueckii',
    name: 'Lactobacillus delbrueckii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 43, max: 45 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "A homofermentative Lactobacillus giving clean lactic sourness and nothing else, and the standard choice for kettle souring. BSI publish 110–115 °F (43–46 °C); the upper bound is held at this catalog's 45 °C ceiling. No attenuation or flocculation is published and the attenuation figure here is nominal.",
    tags: ['lactobacillus', 'sour', 'kettle-sour', 'bacteria', 'clean', 'berliner-weisse', 'gose'],
    url: 'https://brewingscience.com/product/lactobacillus-delbrueckii/',
    provenance: 'representative',
  },
  {
    id: 'bsi-pediococcus-damnosus',
    lab: 'Brewing Science Institute',
    productCode: 'P. damnosus',
    name: 'Pediococcus damnosus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 18, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: false,
    description:
      'Pediococcus for barrel-aged sours: relatively tolerant of both hops and alcohol, slow to acidify, and prone to a temporary ropiness that ages out. It makes diacetyl, so BSI advise pitching it alongside a Brettanomyces culture to clean up after it. BSI publish no attenuation, temperature or flocculation, so those three figures are ours.',
    tags: ['pediococcus', 'sour', 'bacteria', 'barrel-aged', 'lactic', 'mixed-culture'],
    url: 'https://brewingscience.com/product/pediococcus-damnosus/',
    provenance: 'representative',
  },
];
